import React, { useState } from 'react';
import { Eye, EyeOff, LogIn, Mail, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ 
  onSuccess, 
  onSwitchToRegister 
}) => {
  const { showSuccess, showError } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      showError('Por favor completa todos los campos');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await login({ email, password });
      
      if (result.success) {
        showSuccess('¡Bienvenido de vuelta!');
        onSuccess?.();
      } else {
        showError(result.message || 'Error al iniciar sesión');
      }
    } catch (error) {
      showError('Error de conexión');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-stone-900 rounded-2xl shadow-gum-lg border border-stone-700 p-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center mb-4 shadow-gum">
            <LogIn className="w-6 h-6 text-stone-900" />
          </div>
          <h2 className="text-2xl font-bold text-stone-100">Iniciar Sesión</h2>
          <p className="text-stone-400 mt-2">Accede a tu cuenta de Ads Finder</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-stone-300 mb-2">
              Correo Electrónico
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-500" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-stone-600 rounded-lg bg-stone-950 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors text-stone-100 placeholder-stone-500"
                placeholder="tu@email.com"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-stone-300 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-500" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-stone-600 rounded-lg bg-stone-950 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors text-stone-100 placeholder-stone-500"
                placeholder="••••••••"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-500 hover:text-stone-300"
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-stone-100 text-stone-900 py-3 px-4 rounded-full font-semibold hover:bg-stone-200 focus:ring-2 focus:ring-secondary-400 focus:ring-offset-2 focus:ring-offset-stone-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-gum border border-stone-300"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-stone-900 border-t-transparent rounded-full animate-spin" />
                <span>Iniciando sesión...</span>
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                <span>Iniciar Sesión</span>
              </>
            )}
          </button>
        </form>

        {onSwitchToRegister && (
          <div className="mt-6 text-center">
            <p className="text-stone-400">
              ¿No tienes una cuenta?{' '}
              <button
                onClick={onSwitchToRegister}
                className="text-secondary-400 hover:text-secondary-300 font-medium"
                disabled={isSubmitting}
              >
                Regístrate aquí
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
